import React, { useState, ReactElement } from 'react';
import { Button } from '@uhg-harmony/uitk-react';
// Alias shared Icon, remove duplicate imports
// import { Icon as SharedIcon } from 'shared/theme/link-ui-react/Icon';
import { ButtonWidgetProps, Config } from './types';
import styled from 'styled-components';
import useFormSubmit from '../Form/useFormSubmit';
import {
  getBorderData,
  getLineColor,
  getRadius,
  getThickness,
} from 'builder/components/WidgetConfigurations/Border';
import { StyledDiv, StyledLink } from '../Link/LinkWidget';
import { buttonRadiusMultiplier } from 'builder/util/constants';
import {
  getAlign,
  getBold,
  getCharacterSpacing,
  getFontFamily,
  getFontSize,
  getItalic,
  getLineSpacing,
  getStrikeThrough,
  getTextColor,
  getUnderline,
} from 'builder/components/WidgetConfigurations/Typography/Typography';
import { getBackgroundColor } from 'builder/components/WidgetConfigurations/Background/Background';
import { handleLinkClick } from '../Image/ImageWidget';
import { horizonUhcTheme } from 'builder/scenes/SiteBuilder/SiteStylesPanel/BrandThemes';
import { track } from 'shared/util/analytics/track';
import { useTrackWidget } from 'shared/util/analytics/useTrackWidget';
import { Icon } from 'shared/theme/link-ui-react/Icon';

const StyledButton = styled(Button)<{
  isUhcTheme: boolean;
  font: string;
  align: string;
  borderColor: string;
  borderThickness: number;
  borderRadius: number;
  borderData: any;
  backgroundColor: string;
  hoverBold: boolean;
  hoverItalic: boolean;
  hoverUnderline: boolean;
  hoverStrikeThrough: boolean;
  hoverFontFamily: string;
  hoverTextColor: string;
  hoverFontSize: number;
  hoverCharacterSpacing: any;
  hoverLineSpacing: any;
  hoverAlign: string;
  hoverBorderColor: string;
  hoverBorderThickness: number;
  hoverBorderRadius: number;
  hoverBorderData: any;
  hoverBackgroundColor: string;
}>`
  padding-top: ${p => (p.isUhcTheme ? '0.5rem' : '0px')};
  font-family: ${p => p.font} !important;
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: ${p =>
    p.align === 'left'
      ? 'left'
      : p => (p.align === 'right' ? 'right' : 'center')};
  border-style: solid;
  border-top: ${p =>
    p.borderData?.isTopBorderVisible ?? true
      ? `${p.borderThickness}px solid`
      : 'none'};
  border-bottom: ${p =>
    p.borderData?.isBottomBorderVisible ?? true
      ? `${p.borderThickness}px solid`
      : 'none'};
  border-left: ${p =>
    p.borderData?.isLeftBorderVisible ?? true
      ? `${p.borderThickness}px solid`
      : 'none'};
  border-right: ${p =>
    p.borderData?.isRightBorderVisible ?? true
      ? `${p.borderThickness}px solid`
      : 'none'};
  border-color: ${p => p.borderColor};
  border-radius: ${p => p.borderRadius}px;
  background-color: ${p => p.backgroundColor};
  width: 100%;
  height: 100%;
  min-height: 2.5rem;
  :hover,
  :focus {
    font-weight: ${p => (p.hoverBold ? 'bold' : 'normal')} !important;
    font-style: ${p => (p.hoverItalic ? 'italic' : 'normal')} !important;
    text-decoration-line: ${p => (p.hoverUnderline ? 'underline' : '')}
      ${p => (p.hoverStrikeThrough ? 'line-through' : '')} !important;
    font-family: ${p => p.hoverFontFamily} !important;
    font-size: ${p => p.hoverFontSize}px !important;
    color: ${p => p.hoverTextColor} !important;
    letter-spacing: ${p => p.hoverCharacterSpacing}px !important;
    line-height: ${p => p.hoverLineSpacing} !important;
    justify-content: ${p =>
      p.hoverAlign === 'left'
        ? 'left'
        : p => (p.hoverAlign === 'right' ? 'right' : 'center')};
    border-top: ${p =>
      p.hoverBorderData?.isTopBorderVisible ?? true
        ? `${p.hoverBorderData?.thickness}px solid`
        : 'none'};
    border-bottom: ${p =>
      p.hoverBorderData?.isBottomBorderVisible ?? true
        ? `${p.hoverBorderData?.thickness}px solid`
        : 'none'};
    border-left: ${p =>
      p.hoverBorderData?.isLeftBorderVisible ?? true
        ? `${p.hoverBorderData?.thickness}px solid`
        : 'none'};
    border-right: ${p =>
      p.hoverBorderData?.isRightBorderVisible ?? true
        ? `${p.hoverBorderData?.thickness}px solid`
        : 'none'};
    border-color: ${p => p.hoverBorderColor};
    border-radius: ${p => p.hoverBorderRadius}px;
    background-color: ${p => p.hoverBackgroundColor};
  }
`;

const StyledButtonWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.25rem;
  z-index: 1;
`;

const IconImage = styled.img<{ maxWidth: number; maxHeight: number }>`
  max-width: ${p => p.maxWidth}px;
  max-height: ${p => p.maxHeight}px;
`;

const IconLeft = styled.span`
  padding-right: 0.25rem;
`;
const IconRight = styled.span`
  padding-left: 0.25rem;
`;

export const ButtonWidget: React.FC<WidgetRenderProps<Config> &
  ButtonWidgetProps<Config>> = (
  props: WidgetRenderProps<Config> & ButtonWidgetProps<Config>
): ReactElement => {
  const { editing, currentPage, site } = props;
  const {
    action,
    displayText,
    hoverData,
    url,
    newTab,
    iconColor,
    currentTab,
    bold,
    iconAltText,
    visible,
    isCustomWidget,
    linkType,
    buttonAnalyticsText,
  } = props.widgetState.config;
  const { widgetState } = props;
  const [hover, setHover] = useState(false);
  const [backgroundData] = useState(props.widgetState.config.backgroundData);
  const { handleFormData } = useFormSubmit(props);
  // Standardized tracking hook
  const trackButtonWidget = useTrackWidget({
    tracking: props.tracking,
    widgetState,
    currentPage,
    site,
  });

  const activeBackgroundColor = getBackgroundColor(widgetState, currentTab);

  function submitAction() {
    trackButtonWidget('button_widget_click', {
      buttonWidgetLabel: buttonAnalyticsText ?? displayText,
      buttonPage: currentPage.slug,
      buttonSite: site.domain,
    });

    if (action === 'submit' && !props.editing) {
      handleFormData();
    } else {
      return;
    }
  }

  function handleClick() {
    submitAction();
    handleLinkClick(linkType, editing);
  }

  // Removed duplicate unused hook instance (was trackWidget)

  function renderIconLeft(widgetState, tab, hover) {
    const { icon, iconStyle, iconPlacement, hoverData } = widgetState.config;
    if (tab === 0) {
      switch (hover) {
        case true:
          return (
            hoverData.icon &&
            hoverData.iconStyle === 'With Icon' &&
            hoverData.iconPlacement === 'Left' && (
              <IconLeft>
                {hoverData.icon.url && (
                  <IconImage
                    src={hoverData.icon.url}
                    maxWidth={50}
                    maxHeight={50}
                    draggable={false}
                    aria-label={hoverData.iconAltText}
                    alt={hoverData.iconAltText}
                  />
                )}
              </IconLeft>
            )
          );
        case false:
          return (
            icon &&
            iconStyle === 'With Icon' &&
            iconPlacement === 'Left' && (
              <IconLeft>
                {icon.url && (
                  <IconImage
                    src={icon.url}
                    maxWidth={50}
                    maxHeight={50}
                    draggable={false}
                    aria-label={iconAltText}
                    alt={iconAltText}
                  />
                )}
              </IconLeft>
            )
          );
        default:
      }
    }
    if (tab === 1) {
      return (
        hoverData.icon &&
        hoverData.iconStyle === 'With Icon' &&
        hoverData.iconPlacement === 'Left' && (
          <IconLeft>
            {hoverData.icon.url && (
              <IconImage
                src={hoverData.icon.url}
                maxWidth={50}
                maxHeight={50}
                draggable={false}
                aria-label={hoverData.iconAltText}
                alt={hoverData.iconAltText}
              />
            )}
          </IconLeft>
        )
      );
    }
  }

  function renderIconRight(widgetState, tab, hover) {
    const { icon, iconStyle, iconPlacement, hoverData } = widgetState.config;
    if (tab === 0) {
      switch (hover) {
        case true:
          return (
            hoverData.icon &&
            hoverData.iconStyle === 'With Icon' &&
            hoverData.iconPlacement === 'Right' && (
              <IconRight>
                {hoverData.icon.url && (
                  <IconImage
                    src={hoverData.icon.url}
                    maxWidth={50}
                    maxHeight={50}
                    draggable={false}
                    aria-label={hoverData.iconAltText}
                    alt={hoverData.iconAltText}
                  />
                )}
              </IconRight>
            )
          );
        case false:
          return (
            icon &&
            iconStyle === 'With Icon' &&
            iconPlacement === 'Right' && (
              <IconRight>
                {icon.url && (
                  <IconImage
                    src={icon.url}
                    maxWidth={50}
                    maxHeight={50}
                    draggable={false}
                    aria-label={iconAltText}
                    alt={iconAltText}
                  />
                )}
              </IconRight>
            )
          );
        default:
      }
    }
    if (tab === 1) {
      return (
        hoverData.icon.url &&
        hoverData.iconStyle === 'With Icon' &&
        hoverData.iconPlacement === 'Right' && (
          <IconRight>
            {hoverData.icon.url && (
              <IconImage
                src={hoverData.icon.url}
                maxWidth={50}
                maxHeight={50}
                draggable={false}
                aria-label={hoverData.iconAltText}
                alt={hoverData.iconAltText}
              />
            )}
          </IconRight>
        )
      );
    }
  }

  function displayButtonText(bold) {
    return bold ? <strong>{displayText}</strong> : displayText;
  }

  if (visible === false || isCustomWidget) {
    return <></>;
  }

  // Compute background styles to simplify nested ternaries
  const isColorValue =
    activeBackgroundColor.startsWith('#') ||
    activeBackgroundColor.startsWith('rgb');
  let computedBackgroundColor = '';
  let computedBackgroundImage = '';
  if (backgroundData?.style === 'Color') {
    // For color values, use as backgroundColor when not hover
    if (!hover && isColorValue) computedBackgroundColor = activeBackgroundColor;
    // For non-color values (e.g. gradient string), apply on hover as backgroundColor
    if (hover && !isColorValue) computedBackgroundColor = activeBackgroundColor;
    // If not hover and non-color value, treat as backgroundImage
    if (!hover && !isColorValue)
      computedBackgroundImage = activeBackgroundColor;
    // If hover and color value, treat as backgroundImage (mirroring original logic)
    if (hover && isColorValue) computedBackgroundImage = activeBackgroundColor;
  }

  return (
    <StyledButtonWrapper>
      <StyledButton
        isUhcTheme={
          site?.theme?.colors.primary === horizonUhcTheme.colors.primary
        }
        tabIndex={0}
        onClick={handleClick}
        as={StyledLink}
        data-test-id="button-widget-with-link"
        icon={null}
        href={editing || url === '' ? null : url}
        target={newTab ? '_blank' : ''}
        font={getFontFamily(widgetState, currentTab)}
        fontSize={getFontSize(widgetState, currentTab)}
        textColor={getTextColor(widgetState, currentTab)}
        characterSpacing={getCharacterSpacing(widgetState, currentTab)}
        lineSpacing={getLineSpacing(widgetState, currentTab)}
        strikeThrough={getStrikeThrough(widgetState, currentTab)}
        italic={getItalic(widgetState, currentTab)}
        bold={getBold(widgetState, currentTab)}
        underline={getUnderline(widgetState, currentTab)}
        onMouseOver={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        align={getAlign(widgetState, currentTab)}
        borderColor={getLineColor(widgetState, currentTab)}
        borderThickness={getThickness(widgetState, currentTab)}
        borderRadius={
          getRadius(widgetState, currentTab) * buttonRadiusMultiplier
        }
        borderData={getBorderData(widgetState, currentTab)}
        backgroundColor={getBackgroundColor(widgetState, currentTab)}
        hoverFontFamily={hoverData.font}
        hoverTextColor={hoverData.textColor}
        hoverFontSize={hoverData.fontSize}
        hoverBold={hoverData.bold}
        hoverItalic={hoverData.italic}
        hoverUnderline={hoverData.underline}
        hoverStrikeThrough={hoverData.strikeThrough}
        hoverCharacterSpacing={hoverData.characterSpacing}
        hoverLineSpacing={hoverData.lineSpacing}
        hoverAlign={hoverData.align}
        hoverBorderColor={hoverData.lineColor}
        hoverBorderThickness={hoverData.borderData.thickness}
        hoverBorderRadius={hoverData.borderData.radius * buttonRadiusMultiplier}
        hoverBackgroundColor={hoverData.backgroundData.color}
        hoverBorderData={hoverData.borderData}
        style={{
          backgroundColor: computedBackgroundColor,
          backgroundImage: computedBackgroundImage,
        }}
      >
        {renderIconLeft(widgetState, currentTab, hover)}
        {displayButtonText(bold)}
        {newTab && (
          <StyledDiv>
            <Icon
              icon="NewWindowDarkArrow"
              width={'1em'}
              height={'.9em'}
              viewBox="0 0 15 15"
              style={{ fill: `${iconColor}` }}
            />
          </StyledDiv>
        )}
        {renderIconRight(widgetState, currentTab, hover)}
      </StyledButton>
    </StyledButtonWrapper>
  );
};

export default track()(ButtonWidget);
